import { request } from './client.js';
import type {
  ReconsentResult,
  SentinelOneResponseToken,
  ClientTenant,
  CreateTenantResult,
  ListTenantsResult,
  SentinelOneCredentials,
  ServiceKey,
} from './types.js';

/**
 * The Clients the caller may see. `includeOperations` adds the
 * platform-operations tenant (`kind: 'operations'`) for a caller with scope
 * `all` (task 200): only the Client switcher asks, so an operations alert
 * can be opened. It is not a Client, so no management screen should.
 */
export function listTenants(
  idToken: string,
  options: { includeOperations?: boolean } = {},
): Promise<ListTenantsResult> {
  return request<ListTenantsResult>('GET', '/tenants', idToken, {
    query: options.includeOperations ? { include: 'operations' } : undefined,
  });
}

/**
 * A pending Client beneath `parentOrgId` (an active MSP, or Simulint, in the
 * caller's subtree; default their home). 409 `not_resellable` when that
 * organization does not resell MDR.
 */
export function createTenant(
  displayName: string,
  idToken: string,
  options: { parentOrgId?: string; services?: ServiceKey[] } = {},
): Promise<CreateTenantResult> {
  return request<CreateTenantResult>('POST', '/tenants', idToken, {
    body: {
      display_name: displayName,
      ...(options.parentOrgId ? { parent_org_id: options.parentOrgId } : {}),
      ...(options.services ? { services: options.services } : {}),
    },
  });
}

export function revokeTenant(clientTenantId: string, idToken: string): Promise<ClientTenant> {
  return request<ClientTenant>('DELETE', `/tenants/${encodeURIComponent(clientTenantId)}`, idToken);
}

/**
 * Store a Client's SentinelOne credentials. The API validates them against
 * the console before saving, and never returns the token; the response is
 * the Client record with the source registered.
 */
export function putSentinelOneSource(
  clientTenantId: string,
  credentials: SentinelOneCredentials,
  idToken: string,
  options: { addService?: boolean } = {},
): Promise<ClientTenant> {
  return request<ClientTenant>(
    'PUT',
    `/tenants/${encodeURIComponent(clientTenantId)}/sources/sentinelone`,
    idToken,
    { body: credentials, query: addServiceQuery(options) },
  );
}

/**
 * `?add_service=true`: a platform Admin's confirmation, after a 409
 * `service_required`, to add the covering service with the connect (task 179).
 */
function addServiceQuery(options: { addService?: boolean }): Record<string, string> | undefined {
  return options.addService ? { add_service: 'true' } : undefined;
}

/**
 * Switch on Entra sign-in and audit logs (task 157). No body: it rides the
 * Defender app's consent. A 409 `consent_required` means the Client must
 * first grant AuditLog.Read.All through Update permissions.
 */
export function putEntraLogsSource(
  clientTenantId: string,
  idToken: string,
  options: { addService?: boolean } = {},
): Promise<ClientTenant> {
  return request<ClientTenant>(
    'PUT',
    `/tenants/${encodeURIComponent(clientTenantId)}/sources/entra_logs`,
    idToken,
    { query: addServiceQuery(options) },
  );
}

export function removeSource(
  clientTenantId: string,
  source: string,
  idToken: string,
): Promise<ClientTenant> {
  return request<ClientTenant>(
    'DELETE',
    `/tenants/${encodeURIComponent(clientTenantId)}/sources/${encodeURIComponent(source)}`,
    idToken,
  );
}

/**
 * Re-mint a pending Client's consent link. The previous invite token stops
 * working; the response carries the fresh consent URL, the only time it is
 * shown, exactly as on create.
 */
export function reissueInvite(
  clientTenantId: string,
  idToken: string,
): Promise<CreateTenantResult> {
  return request<CreateTenantResult>(
    'POST',
    `/tenants/${encodeURIComponent(clientTenantId)}/invite`,
    idToken,
  );
}

/**
 * A fresh admin-consent URL for a consented Client whose consent predates
 * permissions the app now declares (`missing_scopes` on the Client). The
 * callback records the new scope list; `connected_at` does not move.
 */
export function reconsentTenant(clientTenantId: string, idToken: string): Promise<ReconsentResult> {
  return request<ReconsentResult>(
    'POST',
    `/tenants/${encodeURIComponent(clientTenantId)}/reconsent`,
    idToken,
  );
}

/** platform_admin: the write-scoped SentinelOne token, validated against the console before it is stored; never echoed. */
export function putSentinelOneResponseToken(
  clientTenantId: string,
  body: SentinelOneResponseToken,
  idToken: string,
): Promise<ClientTenant> {
  return request<ClientTenant>(
    'PUT',
    `/tenants/${encodeURIComponent(clientTenantId)}/sources/sentinelone/response-token`,
    idToken,
    { body },
  );
}

export function removeSentinelOneResponseToken(
  clientTenantId: string,
  idToken: string,
): Promise<ClientTenant> {
  return request<ClientTenant>(
    'DELETE',
    `/tenants/${encodeURIComponent(clientTenantId)}/sources/sentinelone/response-token`,
    idToken,
  );
}
